import {ColumnsType} from "antd/lib/table";
import {
    DeliveryMethod,
    OrderPaymentMethod,
    OrderProductDto,
    OrderProductUpdateDto,
    OrderStage,
    ProductPriceDto
} from "../../api";
import {CrudTableWrapperProps} from "digimuth-components/dist/components/crudTable/crudTable";
import {AsyncButton, AsyncButtonPopconfirm, CrudTable, nameof} from "digimuth-components/dist";
import styles from "./orderProductListContainer.module.scss";
import {useEffect, useState} from "react";

interface OrderProductsListProps extends CrudTableWrapperProps<OrderProductDto> {
    productPrice: ProductPriceDto[],
    onFinalizeOrder: () => void,
    orderStage: OrderStage,
    paymentMethod: OrderPaymentMethod,
    deliveryMethod: DeliveryMethod,
    overStock: boolean,
    onAmountChange: (product: OrderProductUpdateDto, id: number) => Promise<any>;
}

export default function OrderProductsList(props: OrderProductsListProps) {
    const [oldData, setOldData] = useState<OrderProductDto[] | null>(null);

    const columns: ColumnsType<OrderProductDto> = [
        {
            key: nameof<OrderProductDto>("unit"),
            dataIndex: nameof<OrderProductDto>("unit"),
            title: "Jednostka miary"
        }, {
            key: nameof<OrderProductDto>("amount"),
            dataIndex: nameof<OrderProductDto>("amount"),
            title: "Ilość"
        }, {
            key: nameof<OrderProductDto>("productName"),
            dataIndex: nameof<OrderProductDto>("productName"),
            title: "Nazwa produktu"
        }, {
            key: nameof<OrderProductDto>("unitPrice"),
            dataIndex: nameof<OrderProductDto>("unitPrice"),
            title: "Cena jednostkowa (zł)",
            render: (price: number) => price.toFixed(2)
        }
    ];

    const canChangeOrder = () => {
        switch (props.orderStage) {
            case OrderStage.New:
                return true;
            case OrderStage.WaitingForPayment:
            case OrderStage.Confirmed:
                return props.paymentMethod.value === "ShopPayment";
            default:
                return false;
        }
    }

    const canAddProduct = (productId: number, currentAmount: number) => {
        const currentProductPrice = props.productPrice.find(x => x.productId === productId);
        if ((props.orderStage === OrderStage.WaitingForPayment || props.orderStage === OrderStage.Confirmed)
            && props.paymentMethod.value === "ShopPayment") {
            return (currentProductPrice?.unitStock ?? 0) > 0;
        }
        return currentAmount! < (currentProductPrice?.unitStock ?? 0)
    }

    useEffect(() => {
        if (props.overStock) setOldData(props.data ?? []);
    }, [props.overStock]);

    return <CrudTable
        data={props.data}
        onAddClicked={canChangeOrder() ? props.onAddClicked : undefined}
        isLoading={props.isLoading}
        columns={columns}
        addButtonTitle={"+ Dodaj nowy produkt do zamówienia"}
        editButtonTitle="Edytuj"
        tableProps={{
            footer: () => <div className={styles.tableFooterContainer}>
                <div>
                    Kwota produktów:&nbsp;
                    <strong>{props.data
                        ?.reduce((acc: number, curr: OrderProductDto) => acc + (curr.unitPrice! * curr.amount!), 0).toFixed(2)} zł
                    </strong>
                </div>
                <AsyncButtonPopconfirm
                    title={"Czy na pewno chcesz sfinalizować to zamówienie?"}
                    okText={"Finalizuj"}
                    cancelText={"Anuluj"}
                    type={"primary"}
                    disabled={!props.data || props.data.length === 0 || props.orderStage !== OrderStage.New}
                    onConfirm={props.onFinalizeOrder}
                >
                    Finalizuj zamówienie
                </AsyncButtonPopconfirm>
            </div>
        }}
        deleteConfirmMessage="Czy na pewno chcesz usunąć ten produkt?"
        customRowActions={record => <>
            <div className={styles.quantitySelectorContainer}>
                <AsyncButton
                    disabled={record.amount === 1 || !canChangeOrder()}
                    onClick={() => props.onAmountChange({
                        amount: record.amount! - 1,
                        productId: record.productId
                    }, record.id!)}
                >
                    <strong>{'\u2212'}</strong>
                </AsyncButton>
                <div className={styles.quantity}>
                    <div style={{fontWeight: oldData ? "unset" : "bold"}}>
                        {record.amount}
                    </div>
                    {oldData && <strong>
                        ({record.amount! - (oldData.find(x => x.productId == record.productId)?.amount ?? 0)})
                    </strong>}
                </div>
                <AsyncButton
                    disabled={!canAddProduct(record.productId!, record.amount!) || !canChangeOrder()}
                    onClick={() => props.onAmountChange({
                        amount: record.amount! + 1,
                        productId: record.productId
                    }, record.id!)}
                >
                    <strong>+</strong>
                </AsyncButton>
            </div>
            <AsyncButtonPopconfirm
                title={"Czy na pewno chcesz usunąć ten produkt z koszyka?"}
                danger
                okText={"Usuń"}
                disabled={!canChangeOrder()}
                cancelText={"Anuluj"}
                onConfirm={() => props.onDeleteClicked!(record.id!)}
            >
                Usuń
            </AsyncButtonPopconfirm>
        </>
        }
    />;
}
